<template>
  <v-app class="app">
    <v-main>
      <router-view/>

      <!-- Connection / Session Snackbar -->
      <v-snackbar
        v-model="connectionAlert.show"
        :color="connectionAlert.color"
        location="bottom right"
        :timeout="-1"
        min-width="320"
        max-width="420"
        rounded="lg"
        elevation="4"
      >
        <div class="d-flex align-center gap-3">
          <v-icon size="20">{{ connectionAlert.icon }}</v-icon>
          <div class="flex-1 ml-4">
            <div class="font-weight-medium text-body-2">{{ connectionAlert.title }}</div>
            <div class="text-caption opacity-75 mt-1">{{ connectionAlert.message }}</div>
          </div>
        </div>

        <template v-slot:actions>
          <!-- Retry countdown — connection/server errors only -->
          <v-chip
            v-if="!connectionAlert.isExpired && !connectionAlert.isDeactivated"
            size="x-small"
            variant="tonal"
            class="mr-1"
          >
            {{ healthCountdown }}s
          </v-chip>

          <!-- Session expired -->
          <v-btn
            v-if="connectionAlert.isExpired"
            size="small"
            variant="tonal"
            @click="forceLogout"
          >
            Log in again
          </v-btn>

          <!-- Deactivated — auto logout countdown -->
          <v-chip v-if="connectionAlert.isDeactivated" size="x-small" variant="tonal" class="mr-1">
            Logging out in {{ logoutCountdown }}s
          </v-chip>

          <v-btn icon size="x-small" variant="plain" @click="connectionAlert.show = false">
            <v-icon size="16">mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <!-- Global Snackbar -->
      <v-snackbar
        v-model="snackbar"
        :timeout="2500"
        :color="snackColor"
        location="top"
        transition="slide-y-transition"
      >
        <v-row dense>
          <v-col cols="1"><v-icon>mdi-alert-circle</v-icon></v-col>
          <v-col cols="11"><span style="white-space: pre-line;">{{ snackText }}</span></v-col>
        </v-row>
      </v-snackbar>

    </v-main>
  </v-app>
</template>

<script>
import { useSnackbar } from './composables/useSnackbar'
import axios from 'axios'
import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "@/assets/vfs_fonts";

pdfMake.vfs = vfs;
pdfMake.fonts = {
  ArialNarrow: {
    normal: "arialnarrow.ttf",
    bold: "arialnarrow_bold.ttf",
    italics: "arialnarrow_italic.ttf",
    bolditalics: "arialnarrow_bolditalic.ttf"
  },
  TimesNewRoman: {
    normal: "times-new-roman-bold-italic.ttf",
    bold: "times-new-roman-bold-italic.ttf",
    italics: "times-new-roman-bold-italic.ttf",
    bolditalics: "times-new-roman-bold-italic.ttf"
  },
  CenturySchoolBook: {
    normal: "century-schoolbook-bolditalics.ttf",
    bold: "century-schoolbook-bolditalics.ttf",
    italics: "century-schoolbook-bolditalics.ttf",
    bolditalics: "century-schoolbook-bolditalics.ttf"
  }
};

// ── Health check: pings /notifications every 60s → drives connection alerts
const HEALTH_INTERVAL_MS    = 60 * 1000

// ── Slow response: warn if no reply within 5s
const SLOW_THRESHOLD_MS     = 5 * 1000

// ── Token refresh: fire this many ms before the token expires
// Production ('1d'):  set to 2 * 60 * 1000  (2 minutes)
// Testing   ('60s'):  set to 10 * 1000  (10 seconds)
const REFRESH_BEFORE_EXP_MS = 2 * 60 * 1000

// ── Deactivated: grace period before forced logout
const LOGOUT_COUNTDOWN_SEC  = 10

export default {
  name: 'App',

  setup() {
    const { snackbar, snackText, snackColor } = useSnackbar()
    return { snackbar, snackText, snackColor }
  },

  data: () => ({
    connectionAlert: {
      show:          false,
      color:         'error',
      icon:          '',
      title:         '',
      message:       '',
      isExpired:     false,
      isDeactivated: false,
      serverWasDown: false,
    },

    healthCountdown:  HEALTH_INTERVAL_MS / 1000,  // counts 60 → 0 for display
    logoutCountdown:  LOGOUT_COUNTDOWN_SEC,        // counts 10 → 0 then logout

    // Health check timers
    healthInterval:   null,  // setInterval  — fires pingServer every 30s
    healthCountTick:  null,  // setInterval  — ticks healthCountdown display
    slowTimer:        null,  // setTimeout   — triggers slow-response alert

    // Token refresh timer
    refreshTimer:     null,  // setTimeout   — fires ~2min before token expires

    // Logout timer
    logoutTick:       null,  // setInterval  — counts down then calls forceLogout
  }),

  mounted() {
    window.addEventListener('online',  this.onBrowserOnline)
    window.addEventListener('offline', this.onBrowserOffline)

    this.startHealthCycle()    // connection alerts — every 30s
    this.scheduleTokenRefresh() // silent token refresh — expiry-based
  },

  beforeUnmount() {
    window.removeEventListener('online',  this.onBrowserOnline)
    window.removeEventListener('offline', this.onBrowserOffline)
    this.stopHealthCycle()
    clearTimeout(this.refreshTimer)
    clearInterval(this.logoutTick)
  },

  methods: {

    // ════════════════════════════════════════════════════════════════
    // CYCLE 1 — HEALTH CHECK  (every 30s, drives the alert snackbar)
    // Endpoint: GET /notifications/findAllByUser
    // Why: already polling, authenticated, representative of server health
    // ════════════════════════════════════════════════════════════════

    startHealthCycle() {
      this.pingServer()
      this.startHealthCountdown()

      this.healthInterval = setInterval(() => {
        this.pingServer()
        this.resetHealthCountdown()
      }, HEALTH_INTERVAL_MS)
    },

    stopHealthCycle() {
      clearInterval(this.healthInterval)
      clearInterval(this.healthCountTick)
      clearTimeout(this.slowTimer)
    },

    startHealthCountdown() {
      this.healthCountdown = HEALTH_INTERVAL_MS / 1000
      clearInterval(this.healthCountTick)
      this.healthCountTick = setInterval(() => {
        if (this.healthCountdown > 0) this.healthCountdown--
      }, 1000)
    },

    resetHealthCountdown() {
      this.healthCountdown = HEALTH_INTERVAL_MS / 1000
    },

    async pingServer() {
      //if (!navigator.onLine) return

      // Start slow-response timer
      clearTimeout(this.slowTimer)
      this.slowTimer = setTimeout(() => {
        this.showAlert(
          'warning', 'mdi-clock-alert-outline',
          'Slow connection detected',
          'The server is taking longer than usual. Some features may be delayed.',
          false, false
        )
      }, SLOW_THRESHOLD_MS)

      try {
        // Public endpoint — no token needed, works even before login
        // await axios.get(`${process.env.VUE_APP_SERVER_URL}/auth/health`)
        await axios.get(`${process.env.VUE_APP_SERVER_URL}/auth/health`)
        clearTimeout(this.slowTimer)

        // Only clear connection alerts — never wipe session/deactivated banners
        if (!this.connectionAlert.isExpired && !this.connectionAlert.isDeactivated) {
          if (this.serverWasDown) {
            this.serverWasDown = false
            window.location.reload()
            return
          }
          this.clearAlert()
        }
      } catch (error) {
        clearTimeout(this.slowTimer)

        // Double-check onLine here too — network may have dropped mid-request
        if (!navigator.onLine) {
          this.showAlert(
            'error', 'mdi-wifi-off',
            'No internet connection',
            'Check your network. The app will retry automatically.',
            false, false
          )
          return
        }

        if (!error.response || error.response.status >= 500) {
          this.serverWasDown = true
          this.showAlert(
            'error', 'mdi-server-off',
            'AUMS Server Is Down!',
            'The AUMS might be in maintenance or offline due to technical issues. Retrying automatically.',
            false, false
          )
        }
      }
    },

    // ════════════════════════════════════════════════════════════════
    // CYCLE 2 — TOKEN REFRESH  (silent, scheduled from token expiry)
    // Endpoint: POST /auth/refresh-token
    // Token lifespan: '1d' → refreshes once per day, ~2min before expiry
    // ════════════════════════════════════════════════════════════════

    decodeToken(token) {
      try {
        return JSON.parse(atob(token.split('.')[1]))
      } catch {
        return null
      }
    },

    msUntilRefresh(token) {
      const decoded = this.decodeToken(token)
      if (!decoded?.exp) return 0

      const expiresAt  = decoded.exp * 1000
      const tokenLife  = expiresAt - (decoded.iat * 1000)   // total lifespan in ms
      const buffer     = Math.min(REFRESH_BEFORE_EXP_MS, tokenLife * 0.8) // never exceed 80% of lifespan
      const refreshAt  = expiresAt - buffer

      return Math.max(0, refreshAt - Date.now())
    },

    scheduleTokenRefresh() {
      clearTimeout(this.refreshTimer)

      const token = localStorage.getItem('aums-token')
      if (!token) return

      const delayMs = this.msUntilRefresh(token)
      const delayHr = Math.floor(delayMs / (1000 * 60 * 60))
      const delayMin = Math.floor((delayMs % (1000 * 60 * 60)) / (1000 * 60))
      const delaySec = Math.round(delayMs / 1000)
      const delayRemSec = delaySec % 60
      console.log(`[AUMS] Token refresh scheduled in ${delayHr}h ${delayMin}m ${delayRemSec}s`)

      this.refreshTimer = setTimeout(() => this.refreshToken(), delayMs)
    },

    async refreshToken() {
      const token = localStorage.getItem('aums-token')
      if (!token) return

      // If offline, wait until back online — onBrowserOnline will re-schedule
      if (!navigator.onLine) return

      try {
        const res = await axios.post(
          `${process.env.VUE_APP_SERVER_URL}/auth/refresh-token`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        )

        const newToken = res.data?.token
        if (newToken) {
          localStorage.setItem('aums-token', newToken)
          this.$store.commit('UPDATE_USER',  res.data?.user  || null)
          this.$store.commit('UPDATE_EMAIL', res.data?.email || null)
        }

        // Schedule next refresh from the new token's expiry
        this.scheduleTokenRefresh()

      } catch (error) {
        const status = error?.response?.status

        if (status === 401) {
          this.showAlert(
            'error', 'mdi-lock-alert-outline',
            'Session expired',
            'Your session has expired. Please log in again.',
            true, false
          )
          return
        }

        if (status === 400) {
          this.showAlert(
            'error', 'mdi-account-off',
            'Account Deactivated',
            'Your account has been deactivated. Please contact the admin or the VPAA office.',
            false, true
          )
          // Stop everything — no point retrying
          this.stopHealthCycle()
          clearTimeout(this.refreshTimer)
          this.startLogoutCountdown()
          return
        }

        // Server down during refresh — retry in 60s silently, health check will show the alert
        if (!error.response || status >= 500) {
          console.warn('[AUMS] Token refresh failed (server down), retrying in 60s')
          this.refreshTimer = setTimeout(() => this.refreshToken(), 60 * 1000)
        }
      }
    },

    // ════════════════════════════════════════════════════════════════
    // SHARED HELPERS
    // ════════════════════════════════════════════════════════════════

    onBrowserOnline() {
      if (!this.connectionAlert.isExpired && !this.connectionAlert.isDeactivated) {
        this.clearAlert()
      }
      this.pingServer()
      this.scheduleTokenRefresh()
    },

    onBrowserOffline() {
      clearTimeout(this.slowTimer)
      this.showAlert(
        'error', 'mdi-wifi-off',
        'No internet connection',
        'Check your network. The app will retry automatically.',
        false, false
      )
    },

    showAlert(color, icon, title, message, isExpired = false, isDeactivated = false) {
      this.connectionAlert = { show: true, color, icon, title, message, isExpired, isDeactivated }
    },

    clearAlert() {
      this.connectionAlert.show = false
    },

    startLogoutCountdown() {
      this.logoutCountdown = LOGOUT_COUNTDOWN_SEC
      clearInterval(this.logoutTick)
      this.logoutTick = setInterval(() => {
        this.logoutCountdown--
        if (this.logoutCountdown <= 0) {
          clearInterval(this.logoutTick)
          this.forceLogout()
        }
      }, 1000)
    },

    forceLogout() {
      localStorage.removeItem('vuex')
      localStorage.removeItem('aums-token')
      window.location.reload()
    },
  },
}
</script>

<style>
body {
  background-color: '#5B7459';
}

html {
  overflow-y: auto !important;
}

.dt-container {
  border-radius: 10px !important;
}
.dt-container thead th {
  background-color: #1a5b0f;
  color: white !important;
}
.dt-container thead th:first-child { border-radius: 10px 0 0 0; }
.dt-container thead th:last-child  { border-radius: 0 10px 0 0; }

.v-data-table-headers--mobile { display: none; }

.custom-snackbar {
  position: fixed !important;
  top: 50%;
  left: 50%;
  transform: translate(-10%, -51%);
}

@keyframes fadeIn {
  0%   { opacity: 0; transform: translateZ(0); }
  100% { opacity: 1; transform: translateZ(100%); }
}
@keyframes viewUp {
  0%   { opacity: 0; transform: translateY(0); }
  50%  { opacity: 0.5; transform: translateY(12px); }
  100% { opacity: 1; transform: translateY(0); }
}

.doughnut-style { width: 110px; height: 110px; margin: 7px; }
.toggle   { animation-name: fadeIn; }
.animated { animation-duration: 0.8s; }
</style>